/**
 * Blocking and unblocking dates on the host pricing calendar.
 *
 * Spec 014c FR-010: Lenvoy asked that hosts can always block dates. This panel
 * must not read host capabilities or brand rules (`useHostCapabilities`,
 * `useBrandRules`), and no switch or car setting may hide it. The server side
 * is pinned by the backend test
 * `src/components/brand-rules/host-capability.coverage.spec.ts`
 * ("calendar blocking stays open to hosts"); the web side by
 * `e2e/brand-host-capabilities.mocked.spec.ts`.
 */
import React, { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import tw from 'twin.macro';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import {
  CREATE_CAR_AVAILABILITIES_BULK,
  REMOVE_CAR_AVAILABILITIES_BY_DATES,
  GET_CAR_AVAILABILITIES_BY_CAR,
  GET_CAR,
  FIND_BOOKED_RECORD_OF_THE_CAR,
} from '../../services/carService/queries';
import { Button } from '../button';
import { Modal } from '../modal';
import { Field, Select, Tabs, Textarea, Muted } from '../ui';
import { isContiguousSelection, minutesToLabel12h } from '../../helpers/bookedSlots';

const Container = styled.div`
  ${tw`w-full flex flex-col gap-3`}
`;

const Summary = styled.p`
  ${tw`text-sm text-center text-text-secondary m-0`}
`;

const HelpLink = styled.button`
  ${tw`inline-flex items-center justify-center gap-1 text-xs font-medium mx-auto`}
  color: var(--color-black);
  &:hover {
    text-decoration: underline;
  }
`;

const BlockedList = styled.ul`
  ${tw`list-none m-0 p-0 flex flex-col gap-2 max-h-64 overflow-y-auto`}
`;

const BlockedItem = styled.li`
  ${tw`text-sm px-3 py-2 rounded-md`}
  background: var(--color-bg-secondary);
  color: var(--color-text-primary);
`;

const ModalTitle = styled.h3`
  ${tw`text-lg font-semibold m-0 mb-1`}
  font-family: var(--font-display);
  color: var(--color-text-primary);
`;

const ModalBody = styled.div`
  ${tw`flex flex-col gap-4 mt-4`}
`;

const TimeRow = styled.div`
  ${tw`grid grid-cols-2 gap-3`}
`;

const ModalActions = styled.div`
  ${tw`flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-2`}
`;

const FieldRow = styled.div`
  ${tw`grid gap-3`}
  grid-template-columns: 3fr 2fr;
`;

const CheckRow = styled.label`
  ${tw`flex items-center gap-2 text-sm cursor-pointer`}
  color: var(--color-text-primary);
  input {
    width: 16px;
    height: 16px;
  }
`;

const InfoBox = styled.div`
  ${tw`rounded-md p-4 flex gap-3`}
  background: rgb(var(--color-black-rgb) / 0.04);
`;

const InfoIcon = styled.span`
  ${tw`inline-flex items-center justify-center shrink-0 w-5 h-5 rounded-full text-xs font-bold`}
  background: var(--color-black);
  color: var(--color-on-primary);
`;

const InfoTitle = styled.div`
  ${tw`text-sm font-semibold mb-1`}
  color: var(--color-text-primary);
`;

const InfoText = styled.p`
  ${tw`text-sm m-0`}
  color: var(--color-text-secondary);
`;

const DailyNotice = styled.div`
  ${tw`flex flex-col items-center text-center gap-3 py-6 px-2 text-sm`}
  color: var(--color-text-secondary);
`;

/* Four calendar cells with check badges, and a span line for the daily block. */
const Sketch = styled.div<{ $mode: BlockMode }>`
  position: relative;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  width: 100%;
  max-width: 220px;
  height: 56px;
  margin-top: 12px;
  border: 1px solid var(--color-border);
  background: var(--color-bg);
  > i {
    position: relative;
    border-right: 1px solid var(--color-border);
    &:last-child {
      border-right: 0;
    }
    &::before {
      content: '✓';
      position: absolute;
      top: 4px;
      right: 4px;
      width: 12px;
      height: 12px;
      border-radius: 999px;
      font-size: 8px;
      line-height: 12px;
      text-align: center;
      background: var(--color-black);
      color: var(--color-on-primary);
    }
  }
  ${({ $mode }) =>
    $mode === 'hourly' &&
    css`
      > i:nth-child(2)::before,
      > i:nth-child(4)::before {
        display: none;
      }
    `}
  ${({ $mode }) =>
    $mode === 'daily' &&
    css`
      &::after {
        content: '';
        position: absolute;
        left: 12.5%;
        right: 12.5%;
        top: 34px;
        height: 2px;
        background: var(--color-text-tertiary);
      }
      &::before {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        top: 30px;
        height: 10px;
        background:
          radial-gradient(circle at 12.5% 50%, var(--color-text-tertiary) 4px, transparent 4.5px),
          radial-gradient(circle at 87.5% 50%, var(--color-text-tertiary) 4px, transparent 4.5px);
      }
    `}
`;

const HideButton = styled.button`
  ${tw`text-xs font-medium self-end mt-2`}
  color: var(--color-text-secondary);
  &:hover {
    color: var(--color-text-primary);
  }
`;

const NOTE_MAX = 120;
/** "Repeat weekly" copies the block onto the same weekdays for this many weeks. */
const REPEAT_WEEKS = 52;

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => i * 30);

const formatDay = (key: string) => DateTime.fromISO(key).toFormat('ccc, MMM d');

type BlockMode = 'daily' | 'hourly';
type Period = { startAt: number; endAt: number };

interface UnavailabilityPanelProps {
  carId: string;
  /** Calendar days (yyyy-MM-dd) the host selected on the grid. */
  selectedDates: string[];
  setSelectedDates: (dates: string[]) => void;
  /** Calendar days that are already blocked (unavailability ranges or isAvailable = false). */
  blockedDates?: Set<string>;
  timezone?: string;
}

const refetchFor = (carId: string) => [
  { query: GET_CAR_AVAILABILITIES_BY_CAR, variables: { carId } },
  { query: GET_CAR, variables: { id: carId } },
  { query: FIND_BOOKED_RECORD_OF_THE_CAR, variables: { carId, excludeBookingId: '' } },
];

function isAvailabilityOverlap(error: any): boolean {
  const graphQLError = error?.graphQLErrors?.[0];
  const extensions = graphQLError?.extensions as any;
  return (
    extensions?.code === 'AVAILABILITY_OVERLAP' || extensions?.details?.error === 'OVERLAP_CONFLICT'
  );
}

function errorMessage(error: any, fallback: string): string {
  return error?.graphQLErrors?.[0]?.message || error?.message || fallback;
}

/** Whole calendar days (listing timezone) covering each period, for unblocking. */
function dayRangesFor(periods: Period[], timezone: string): Period[] {
  const seen = new Set<number>();
  const ranges: Period[] = [];
  periods.forEach(period => {
    const dayStart = DateTime.fromMillis(period.startAt).setZone(timezone).startOf('day');
    const dayEnd = DateTime.fromMillis(period.endAt).setZone(timezone).endOf('day');
    const range = { startAt: dayStart.toMillis(), endAt: dayEnd.toMillis() };
    if (!seen.has(range.startAt)) {
      seen.add(range.startAt);
      ranges.push(range);
    }
  });
  return ranges;
}

const UnavailabilityPanel: React.FC<UnavailabilityPanelProps> = ({
  carId,
  selectedDates,
  setSelectedDates,
  blockedDates,
  timezone = 'America/Los_Angeles',
}) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [replacePrompt, setReplacePrompt] = useState<{
    periods: Period[];
    note: string | null;
  } | null>(null);
  const [mode, setMode] = useState<BlockMode>('daily');
  const [startDay, setStartDay] = useState('');
  const [endDay, setEndDay] = useState('');
  const [startMinute, setStartMinute] = useState(9 * 60);
  const [endMinute, setEndMinute] = useState(17 * 60);
  const [repeatWeekly, setRepeatWeekly] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(true);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const sortedDays = useMemo(() => [...selectedDates].sort(), [selectedDates]);
  const contiguous = isContiguousSelection(sortedDays);
  const endDayOptions = sortedDays.filter(day => day >= startDay);

  /** Daily needs one continuous run of days; otherwise the sheet opens on Hourly. */
  const openSheet = () => {
    setMode(contiguous ? 'daily' : 'hourly');
    setStartDay(sortedDays[0] ?? '');
    setEndDay(sortedDays[sortedDays.length - 1] ?? '');
    setSheetOpen(true);
  };

  const { data: availabilitiesData } = useQuery(GET_CAR_AVAILABILITIES_BY_CAR, {
    variables: { carId },
    skip: !carId,
  });
  const availabilities: any[] = availabilitiesData?.carAvailabilitiesByCar || [];

  const [createAvailabilitiesBulk] = useMutation(CREATE_CAR_AVAILABILITIES_BULK, {
    refetchQueries: refetchFor(carId),
    awaitRefetchQueries: true,
  });
  const [removeAvailabilitiesByDates] = useMutation(REMOVE_CAR_AVAILABILITIES_BY_DATES, {
    refetchQueries: refetchFor(carId),
    awaitRefetchQueries: true,
  });

  // Fall back to the fetched ranges when the parent did not pass the calendar's blocked set.
  const blockedSet = useMemo(() => {
    if (blockedDates) return blockedDates;
    const set = new Set<string>();
    availabilities.forEach(a => {
      let day = DateTime.fromMillis(Number(a.startAt)).setZone(timezone).startOf('day');
      const end = DateTime.fromMillis(Number(a.endAt)).setZone(timezone).startOf('day');
      while (day <= end) {
        set.add(day.toISODate() || '');
        day = day.plus({ days: 1 });
      }
    });
    return set;
  }, [blockedDates, availabilities, timezone]);

  const selectedCount = selectedDates.length;
  const blockedSelected = selectedDates.filter(date => blockedSet.has(date)).length;

  const summary =
    blockedSelected === 0
      ? `You have not blocked your availability for the selected ${selectedCount === 1 ? 'day' : 'days'}.`
      : `${blockedSelected} of ${selectedCount} selected ${blockedSelected === 1 ? 'day is' : 'days are'} blocked.`;

  const resetSheet = () => {
    setStartMinute(9 * 60);
    setEndMinute(17 * 60);
    setRepeatWeekly(false);
    setNote('');
  };

  const instantAt = (day: string, minute: number) =>
    DateTime.fromISO(day, { zone: timezone }).startOf('day').plus({ minutes: minute }).toMillis();

  /** Weekly repeat: the same block shifted by 7 days, for the next REPEAT_WEEKS weeks. */
  const withRepeats = (base: Period[]): Period[] => {
    if (!repeatWeekly) return base;
    const out: Period[] = [];
    for (let week = 0; week < REPEAT_WEEKS; week++) {
      base.forEach(period => {
        // Shift by whole days in the listing zone so DST does not move the clock time.
        const start = DateTime.fromMillis(period.startAt).setZone(timezone).plus({ weeks: week });
        const end = DateTime.fromMillis(period.endAt).setZone(timezone).plus({ weeks: week });
        out.push({ startAt: start.toMillis(), endAt: end.toMillis() });
      });
    }
    return out;
  };

  const buildPeriods = (): Period[] | null => {
    if (mode === 'daily') {
      // One continuous block from the start day and time to the end day and time.
      if (!startDay || !endDay) return null;
      const startAt = instantAt(startDay, startMinute);
      const endAt = instantAt(endDay, endMinute);
      if (endAt <= startAt) {
        toast.error('The end must be after the start');
        return null;
      }
      return withRepeats([{ startAt, endAt }]);
    }
    if (endMinute <= startMinute) {
      toast.error('End time must be after start time');
      return null;
    }
    // Hourly: the same window on every selected day.
    return withRepeats(
      sortedDays.map(day => ({
        startAt: instantAt(day, startMinute),
        endAt: instantAt(day, endMinute),
      })),
    );
  };

  const runBlock = async (periods: Period[], trimmedNote: string | null) => {
    // Leave `note` out when empty: the server validates it as a string when present.
    await createAvailabilitiesBulk({
      variables: {
        input: {
          carId,
          periods,
          reason: 'HOST_BLOCKED',
          ...(trimmedNote ? { note: trimmedNote } : {}),
        },
      },
    });
  };

  const finishBlock = () => {
    toast.success(
      mode === 'daily'
        ? `Blocked ${formatDay(startDay)} to ${formatDay(endDay)}`
        : `${selectedCount} ${selectedCount === 1 ? 'date' : 'dates'} blocked`,
    );
    setSheetOpen(false);
    resetSheet();
    setSelectedDates([]);
  };

  const submitBlock = async () => {
    const periods = buildPeriods();
    if (!periods || periods.length === 0) return;
    const trimmedNote = note.trim() ? note.trim() : null;
    setBusy(true);
    try {
      await runBlock(periods, trimmedNote);
      finishBlock();
    } catch (error: any) {
      if (isAvailabilityOverlap(error)) {
        setReplacePrompt({ periods, note: trimmedNote });
      } else {
        toast.error(errorMessage(error, 'Failed to block dates'));
      }
    } finally {
      setBusy(false);
    }
  };

  const confirmReplace = async () => {
    if (!replacePrompt) return;
    const { periods, note: trimmedNote } = replacePrompt;
    setReplacePrompt(null);
    setBusy(true);
    try {
      await removeAvailabilitiesByDates({
        variables: { carId, ranges: dayRangesFor(periods, timezone) },
      });
      await runBlock(periods, trimmedNote);
      finishBlock();
    } catch (error: any) {
      toast.error(errorMessage(error, 'Failed to replace the existing block'));
    } finally {
      setBusy(false);
    }
  };

  const unblockSelected = async () => {
    const dates = selectedDates.filter(date => blockedSet.has(date));
    if (dates.length === 0) return;
    const ranges = dates.map(date => {
      const day = DateTime.fromFormat(date, 'yyyy-MM-dd', { zone: timezone });
      return { startAt: day.startOf('day').toMillis(), endAt: day.endOf('day').toMillis() };
    });
    setBusy(true);
    try {
      await removeAvailabilitiesByDates({ variables: { carId, ranges } });
      toast.success(`${dates.length} ${dates.length === 1 ? 'date' : 'dates'} unblocked`);
      setSelectedDates([]);
    } catch (error: any) {
      toast.error(errorMessage(error, 'Failed to unblock dates'));
    } finally {
      setBusy(false);
    }
  };

  const formatPeriod = (startAt: number, endAt: number) => {
    const start = DateTime.fromMillis(Number(startAt)).setZone(timezone);
    const end = DateTime.fromMillis(Number(endAt)).setZone(timezone);
    const time = (d: DateTime) => d.toFormat('h:mma').toLowerCase();
    if (start.hasSame(end, 'day')) {
      return `${start.toFormat('MMM d, yyyy')} · ${time(start)} to ${time(end)}`;
    }
    return `${start.toFormat('MMM d, yyyy')} ${time(start)} to ${end.toFormat('MMM d, yyyy')} ${time(end)}`;
  };

  return (
    <Container>
      <Summary>{summary}</Summary>

      <Button
        text="+ Block availability"
        theme="secondary"
        type="button"
        isDisabled={selectedCount === 0 || busy}
        onClick={openSheet}
      />

      {blockedSelected > 0 && (
        <Button
          text={
            busy
              ? 'Unblocking...'
              : `Unblock ${blockedSelected} ${blockedSelected === 1 ? 'date' : 'dates'}`
          }
          theme="danger"
          type="button"
          isDisabled={busy}
          onClick={unblockSelected}
        />
      )}

      <HelpLink type="button" onClick={() => setHelpOpen(true)}>
        Block pickup and return hours
        <span aria-hidden="true">ⓘ</span>
      </HelpLink>

      {availabilities.length > 0 && (
        <Button
          text={`View blocked periods (${availabilities.length})`}
          theme="text"
          size="sm"
          type="button"
          onClick={() => setListOpen(true)}
        />
      )}

      {/* Block availability sheet */}
      <Modal
        isOpen={sheetOpen}
        onClose={() => {
          if (!busy) setSheetOpen(false);
        }}
        modalWidth="480px"
        title="Block availability"
      >
        <ModalTitle>Block availability</ModalTitle>
        <Muted>
          {selectedCount} {selectedCount === 1 ? 'date' : 'dates'} selected
        </Muted>
        <ModalBody>
          <Tabs
            aria-label="Block mode"
            items={[
              { key: 'daily', label: 'Daily' },
              { key: 'hourly', label: 'Hourly' },
            ]}
            value={mode}
            onChange={setMode}
          />

          {mode === 'daily' && !contiguous ? (
            <DailyNotice>
              <div>
                <strong>Daily</strong> blocks one continuous stretch, from a start day and time to
                an end day and time, for example while the car is in the shop.
              </div>
              <div>
                Your selection skips some days. Switch to <strong>Hourly</strong> to block the same
                time window on each of the selected days, or select days that follow each other.
              </div>
              <Button
                text="Use Hourly"
                theme="secondary"
                size="sm"
                type="button"
                onClick={() => setMode('hourly')}
              />
            </DailyNotice>
          ) : (
            <>
              {mode === 'daily' ? (
                <>
                  <Field label="Start">
                    <FieldRow>
                      <Select
                        value={startDay}
                        onChange={e => {
                          setStartDay(e.target.value);
                          if (endDay < e.target.value) setEndDay(e.target.value);
                        }}
                      >
                        {sortedDays.map(day => (
                          <option key={day} value={day}>
                            {formatDay(day)}
                          </option>
                        ))}
                      </Select>
                      <Select
                        value={startMinute}
                        onChange={e => setStartMinute(Number(e.target.value))}
                      >
                        {TIME_OPTIONS.map(m => (
                          <option key={m} value={m}>
                            {minutesToLabel12h(m)}
                          </option>
                        ))}
                      </Select>
                    </FieldRow>
                  </Field>
                  <Field label="End">
                    <FieldRow>
                      <Select value={endDay} onChange={e => setEndDay(e.target.value)}>
                        {endDayOptions.map(day => (
                          <option key={day} value={day}>
                            {formatDay(day)}
                          </option>
                        ))}
                      </Select>
                      <Select
                        value={endMinute}
                        onChange={e => setEndMinute(Number(e.target.value))}
                      >
                        {TIME_OPTIONS.map(m => (
                          <option key={m} value={m}>
                            {minutesToLabel12h(m)}
                          </option>
                        ))}
                      </Select>
                    </FieldRow>
                  </Field>
                </>
              ) : (
                <TimeRow>
                  <Field label="Start time">
                    <Select
                      value={startMinute}
                      onChange={e => setStartMinute(Number(e.target.value))}
                    >
                      {TIME_OPTIONS.map(m => (
                        <option key={m} value={m}>
                          {minutesToLabel12h(m)}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="End time">
                    <Select value={endMinute} onChange={e => setEndMinute(Number(e.target.value))}>
                      {TIME_OPTIONS.map(m => (
                        <option key={m} value={m}>
                          {minutesToLabel12h(m)}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </TimeRow>
              )}

              <Field label="Notes" hint={`${note.length}/${NOTE_MAX}`}>
                <Textarea
                  rows={2}
                  maxLength={NOTE_MAX}
                  value={note}
                  placeholder="E.g. Maintenance"
                  onChange={e => setNote(e.target.value.slice(0, NOTE_MAX))}
                />
              </Field>

              <CheckRow>
                <input
                  type="checkbox"
                  checked={repeatWeekly}
                  onChange={e => setRepeatWeekly(e.target.checked)}
                />
                Repeat this unavailability weekly
              </CheckRow>

              {showHowItWorks ? (
                <InfoBox>
                  <InfoIcon aria-hidden="true">i</InfoIcon>
                  <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <InfoTitle>How it works</InfoTitle>
                    <InfoText>
                      {mode === 'daily'
                        ? 'One block runs from the start day and time through the end day and time, across every day in between.'
                        : 'The same time window is blocked on each selected day, and the rest of each day stays bookable.'}
                      {repeatWeekly
                        ? ` It repeats on the same weekdays for the next ${REPEAT_WEEKS} weeks.`
                        : ''}
                    </InfoText>
                    <Sketch $mode={mode} aria-hidden="true">
                      <i />
                      <i />
                      <i />
                      <i />
                    </Sketch>
                    <HideButton type="button" onClick={() => setShowHowItWorks(false)}>
                      Hide
                    </HideButton>
                  </div>
                </InfoBox>
              ) : (
                <HideButton type="button" onClick={() => setShowHowItWorks(true)}>
                  How it works
                </HideButton>
              )}
            </>
          )}
          <ModalActions>
            <Button
              text="Cancel"
              theme="secondary"
              type="button"
              isDisabled={busy}
              onClick={() => setSheetOpen(false)}
            />
            <Button
              text={
                mode === 'daily'
                  ? 'Block'
                  : `Block ${selectedCount} ${selectedCount === 1 ? 'date' : 'dates'}`
              }
              type="button"
              isLoading={busy}
              isDisabled={mode === 'daily' && !contiguous}
              onClick={submitBlock}
            />
          </ModalActions>
        </ModalBody>
      </Modal>

      {/* Overlap: replace the old block */}
      <Modal
        isOpen={replacePrompt !== null}
        onClose={() => setReplacePrompt(null)}
        modalWidth="420px"
        title="Already blocked"
      >
        <ModalTitle>Already blocked</ModalTitle>
        <Muted>
          Part of this selection overlaps an existing block. Replace the old block(s) with this one?
        </Muted>
        <ModalActions>
          <Button
            text="Cancel"
            theme="secondary"
            type="button"
            onClick={() => setReplacePrompt(null)}
          />
          <Button text="Replace" type="button" onClick={confirmReplace} />
        </ModalActions>
      </Modal>

      {/* Help */}
      <Modal
        isOpen={helpOpen}
        onClose={() => setHelpOpen(false)}
        modalWidth="420px"
        title="Block pickup and return hours"
      >
        <ModalTitle>Block pickup and return hours</ModalTitle>
        <Muted>
          Use this to block specific pickup and return time windows, such as restricting returns
          after midnight or blocking early morning pickups. Select the dates, choose Hourly, and set
          the window.
        </Muted>
        <ModalActions>
          <Button text="Got it" type="button" onClick={() => setHelpOpen(false)} />
        </ModalActions>
      </Modal>

      {/* Blocked periods list */}
      <Modal
        isOpen={listOpen}
        onClose={() => setListOpen(false)}
        modalWidth="480px"
        title="Blocked periods"
      >
        <ModalTitle>Blocked periods</ModalTitle>
        <Muted>Select the days on the calendar and use Unblock to remove a block.</Muted>
        <ModalBody>
          <BlockedList>
            {availabilities.map(a => (
              <BlockedItem key={a.id}>
                {formatPeriod(a.startAt, a.endAt)}
                {a.note ? <Muted> · {a.note}</Muted> : null}
              </BlockedItem>
            ))}
          </BlockedList>
          <ModalActions>
            <Button
              text="Close"
              theme="secondary"
              type="button"
              onClick={() => setListOpen(false)}
            />
          </ModalActions>
        </ModalBody>
      </Modal>
    </Container>
  );
};

export default UnavailabilityPanel;
